import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

const options = [
  {
    id: 'gcc-setup',
    label: 'A GCC from the ground up',
    description:
      'You have the mandate. You need the founding leader who will set the culture, attract the next 50 people, and navigate the parent organisation from day one.',
    bullets: [
      'Founding Site Head or VP Engineering placement',
      'Leadership cohort sequencing',
      'Operating model advisory before the search opens',
    ],
    cta: 'Explore GCC Practice',
    href: '/gcc-practice',
  },
  {
    id: 'leadership',
    label: 'A technology leadership team',
    description:
      'A VP of Engineering, Head of Data, Director of Product — the leadership layer that sets technical direction, builds the operating model, and hires the next tier.',
    bullets: [
      'CXO, VP and Director-level search',
      'Technical credibility assessment included',
      'Market mapping and salary benchmarking',
    ],
    cta: 'See Tech Leadership Search',
    href: '/services',
  },
  {
    id: 'scaling',
    label: 'A scaling capability function',
    description:
      'Moving from a founding team to a centre of excellence. We build out engineering, data, or product functions with the same intent we brought to the founding search.',
    bullets: [
      'Systematic team build across functions',
      '4-Factor Assessment for every hire',
      'Employer brand and compensation strategy',
    ],
    cta: 'Discuss your mandate',
    href: '/contact',
  },
  {
    id: 'single-hire',
    label: 'A single transformational hire',
    description:
      'One role with outsized impact. Whether it is a Staff Engineer, a Chief Data Officer, or a Country Head — some hires define whether a business succeeds.',
    bullets: [
      'Focused retained search',
      'Full market coverage, not just active candidates',
      'Structured shortlist with evidence-based assessments',
    ],
    cta: 'Start the conversation',
    href: '/contact',
  },
]

export default function WhatAreYouBuildingSection() {
  const [selected, setSelected] = useState(0)
  const active = options[selected]

  return (
    <section
      data-testid="what-building-section"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0B1F3A] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="text-[10px] tracking-[0.38em] uppercase text-white/30 mb-5 font-body font-semibold">
            Where We Start
          </p>
          <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-white">
            What are you<br />
            <span className="font-light text-white/40">building?</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-0 lg:gap-20 items-start">
          {/* Left: option selectors */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="border-t border-white/10"
          >
            {options.map((opt, i) => (
              <button
                key={opt.id}
                data-testid={`building-option-${i + 1}`}
                onClick={() => setSelected(i)}
                className={`w-full text-left py-5 border-b border-white/10 flex items-center justify-between gap-4 group transition-colors duration-200 ${
                  selected === i ? 'text-white' : 'text-white/35 hover:text-white/65'
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className={`text-[9px] font-body font-semibold tracking-[0.25em] flex-shrink-0 transition-colors duration-200 ${selected === i ? 'text-white/50' : 'text-white/18'}`}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm md:text-base font-heading font-semibold leading-tight">
                    {opt.label}
                  </span>
                </div>
                <motion.span
                  animate={{ x: selected === i ? 0 : -4, opacity: selected === i ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-shrink-0"
                >
                  <ArrowRight size={14} className="text-white/50" />
                </motion.span>
              </button>
            ))}
          </motion.div>

          {/* Right: content panel */}
          <div className="mt-12 lg:mt-0 min-h-[240px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                data-testid="building-content-panel"
              >
                <p className="text-base md:text-lg font-body text-white/65 leading-relaxed mb-8 max-w-lg">
                  {active.description}
                </p>
                <ul className="space-y-3 mb-10">
                  {active.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <div className="w-1 h-1 bg-white/30 rounded-full flex-shrink-0 mt-2" />
                      <span className="text-sm font-body text-white/45 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={active.href}
                  data-testid="building-cta"
                  className="inline-flex items-center gap-3 text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-white border-b border-white/25 pb-0.5 hover:border-white hover:text-white transition-colors duration-200"
                >
                  {active.cta} <ArrowRight size={11} />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}
